import { z } from 'zod';

/**
 * Zod schemas for User-related validation
 */

/**
 * Schema for user registration
 */
export const RegisterCredentialsSchema = z.object({
  email: z.string()
    .email('Invalid email address')
    .max(254, 'Email must be less than 254 characters')
    .toLowerCase()
    .trim(),
  
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be less than 128 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/\d/, 'Password must contain at least one number'),
  
  displayName: z.string()
    .min(2, 'Display name must be at least 2 characters')
    .max(50, 'Display name must be less than 50 characters')
    .regex(/^[a-zA-Z0-9\s\-'.]+$/, 'Display name contains invalid characters')
    .trim(),
  
  organizationName: z.string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name must be less than 100 characters')
    .trim(),

  captchaToken: z.string().optional(),
});

export type ValidatedRegisterCredentials = z.infer<typeof RegisterCredentialsSchema>;

/**
 * Schema for user login
 */
export const LoginCredentialsSchema = z.object({
  email: z.string()
    .email('Invalid email address')
    .trim(),
  
  password: z.string()
    .min(1, 'Password is required'),
});

export type ValidatedLoginCredentials = z.infer<typeof LoginCredentialsSchema>;

/**
 * Schema for user profile updates
 */
export const UpdateProfileSchema = z.object({
  displayName: z.string()
    .min(2, 'Display name must be at least 2 characters')
    .max(50, 'Display name must be less than 50 characters')
    .regex(/^[a-zA-Z0-9\s\-'.]+$/, 'Display name contains invalid characters')
    .trim()
    .optional(),
  
  photoURL: z.string()
    .url('Invalid photo URL')
    .max(2048, 'Photo URL too long')
    .optional(),
  
  preferences: z.object({
    theme: z.enum(['light', 'dark']).optional(),
    notifications: z.object({
      email: z.boolean().optional(),
      push: z.boolean().optional(),
      taskAssignment: z.boolean().optional(),
      assignmentHeadsUp: z.boolean().optional(),
    }).optional(),
    timezone: z.string().max(50).optional(),
    language: z.string().max(10).optional(),
  }).optional(),
}).strict();

export type ValidatedUpdateProfile = z.infer<typeof UpdateProfileSchema>;

/**
 * Schema for password reset
 */
export const PasswordResetSchema = z.object({
  email: z.string()
    .email('Invalid email address')
    .trim(),
});

export type ValidatedPasswordReset = z.infer<typeof PasswordResetSchema>;

